"use client"

import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { Images } from "@/constants/images"
import { DefaultImage } from "@/lib/external-components"
import { ensureProtocol } from "@/utils/ensure-protocol-helper"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import Link from "next/link"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import { FooterNavigation } from "./components/FooterNavigation"
import { FooterSocialSection } from "./components/FooterSocialSection"

export interface FooterProps extends CommonStylesProps {
  url?: string
  logoStyles?: {
    width: string
    height: string
    padding: string
    margin: string
    objectFit: "cover" | "contain" | "fill" | "none" | "scale-down"
  }
  footerContainerStyles?: {
    backgroundColor: string
    display: "flex" | "block" | "grid" | "inline"
    flexDirection: "row" | "column"
    gap: string
    justifyContent: "flex-start" | "center" | "flex-end" | "space-between"
    padding: string
    className: string
  }
  footerItemStyles?: {
    titleColor: string
    titleFontSize: string
    titleFontWeight: string
    titleLineHeight: string
    descriptionColor: string
    descriptionFontSize: string
    descriptionFontWeight: string
    descriptionLineHeight: string
    gap: string
  }
  footerItems?: Array<{
    title: string
    subItems?: Array<{ url?: string; subItem: string }>
  }>
  contactInfo?: {
    address?: string
    addressUrl?: string
    phone?: string
    email?: string
    locationIcon?: string
    phoneIcon?: string
    emailIcon?: string
  }
  iconList?: Array<{
    icon: string
    url: string
  }>
  copyRightStyle?: {
    backgroundColor: string
    padding: string
    opacity: string
    color: string
    fontSize: string
    fontWeight: string
    lineHeight: string
  }
  licenceNo?: string
  copyRight?: string
  type: "fluid" | "custom" | "boxed"
}

export const RenderConfig: ComponentConfig<FooterProps> = {
  render: ({
    url,
    logoStyles = {
      width: "120px",
      height: "40px",
      padding: "0px",
      margin: "0px",
      objectFit: "contain",
    },
    className,
    type,
    styles,
    copyRight,
    responsiveType,
    footerItems = [],
    contactInfo,
    iconList,

    footerContainerStyles = {
      backgroundColor: "#E8F5E8",
      display: "flex",
      flexDirection: "row",
      gap: "0px",
      justifyContent: "space-between",
      padding: "60px 120px",
      className: "",
    },
    footerItemStyles = {
      titleColor: "#242F3E",
      titleFontSize: "16px",
      titleFontWeight: "600",
      titleLineHeight: "24px",
      descriptionColor: "#242F3E",
      descriptionFontSize: "14px",
      descriptionFontWeight: "400",
      descriptionLineHeight: "20px",
      gap: "12px",
    },
    copyRightStyle = {
      backgroundColor: "#242F3E",
      padding: "16px",
      opacity: "100%",
      color: "#E8F5E8",
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "20px",
    },
    licenceNo,
  }) => {
    const id = `footer-${Math.random().toString(36).substr(2, 9)}`

    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const lang = document.documentElement.lang
    return (
      <>
        {responsiveCSS}
        <div className="hidden sm:block">
          <div id={id} className={`${className} flex justify-center bg-[#C9E4A5]  `}>
            {/* Main Footer Content */}
            <div className="w-full max-w-[1200px] py-[60px] flex flex-col align-center">
              <div className="flex gap-[80.5px] justify-between">
                {/* Column 1: Logo Section */}
                <div className="flex flex-row items-start gap-4">
                  <Image
                    src={Images.logoCCAGreen}
                    width={parseInt(logoStyles.width) || 120}
                    height={parseInt(logoStyles.height) || 40}
                    style={{
                      objectFit: logoStyles.objectFit,
                      padding: logoStyles.padding,
                      margin: logoStyles.margin,
                    }}
                    className="cursor-pointer object-contain"
                    alt="CaoCao Adventures Logo"
                  />
                </div>

                {/* Footer Items - Dynamic */}
                <FooterNavigation footerItems={footerItems} lang={lang} />

                {/* Column 5: Follow Us On & Contact Info */}
                <div className="flex flex-col gap-4 max-w-[17.9375rem]">
                  {/* Social Links - Dynamic from iconList */}
                  <FooterSocialSection iconList={iconList} />

                  {/* Contact Info - Dynamic from contactInfo */}
                  {contactInfo && (
                    <div className="flex flex-col gap-2">
                      {contactInfo.address && (
                        <div className="flex items-start gap-2">
                          <Image
                            src={
                              (contactInfo?.locationIcon &&
                                `${ADMIN_API_CONFIG.API_URL}${contactInfo?.locationIcon}`) ||
                              DefaultImage
                            }
                            width={16}
                            height={16}
                            alt="Location Icon"
                            className="flex-shrink-0 mt-0.5"
                          />
                          {contactInfo.addressUrl && (
                            <Link
                              href={ensureProtocol(contactInfo.addressUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#242F3E] text-sm leading-relaxed hover:text-[#4A9B4A] transition-colors cursor-pointer break-words whitespace-normal max-w-[210px]"
                            >
                              {contactInfo.address}
                            </Link>
                          )}
                          {!contactInfo.addressUrl && (
                            <span className="text-[#242F3E] text-sm leading-relaxed break-words whitespace-normal max-w-full">
                              {contactInfo.address}
                            </span>
                          )}
                        </div>
                      )}
                      {contactInfo.phone && (
                        <div className="flex items-center gap-2">
                          <Image
                            src={
                              (contactInfo?.phoneIcon && `${ADMIN_API_CONFIG.API_URL}${contactInfo?.phoneIcon}`) ||
                              DefaultImage
                            }
                            width={16}
                            height={16}
                            alt="Phone Icon"
                            className="flex-shrink-0"
                          />
                          <a
                            href={"tel:" + contactInfo.phone}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#242F3E] text-sm hover:text-[#4A9B4A] transition-colors cursor-pointer"
                          >
                            {contactInfo.phone}
                          </a>
                        </div>
                      )}
                      {contactInfo.email && (
                        <div className="flex items-center gap-2">
                          <Image
                            src={
                              (contactInfo?.emailIcon && `${ADMIN_API_CONFIG.API_URL}${contactInfo?.emailIcon}`) ||
                              DefaultImage
                            }
                            width={17}
                            height={14}
                            alt="Email Icon"
                            className="flex-shrink-0"
                          />
                          <a
                            href={"mailto:" + contactInfo.email}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#242F3E] text-sm hover:text-[#4A9B4A] transition-colors cursor-pointer"
                          >
                            {contactInfo.email}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Copyright Section */}
          <div className="w-full bg-[#0C9443] py-4 px-4 flex items-center justify-center">
            <p className="text-[#E8F5E8] text-sm text-center font-normal italic">
              {copyRight || "Copyright 2024 © Cào Cào Adventures. All Rights Reserved"}
            </p>
          </div>
        </div>

        {/* Mobile Footer */}
        <div className="py-10 px-4 bg-[#C9E4A5] flex flex-col gap-6 w-full sm:hidden">
          {/* Logo Section */}
          <div className="flex flex-row items-center gap-4">
            <Image
              src={Images.logoCCAGreen}
              width={parseInt(logoStyles.width) || 120}
              height={parseInt(logoStyles.height) || 40}
              style={{
                objectFit: logoStyles.objectFit,
                padding: logoStyles.padding,
                margin: logoStyles.margin,
              }}
              className="cursor-pointer"
              alt="CaoCao Adventures Logo"
            />
          </div>

          {/* Navigation Sections - 2 Column Layout like Figma */}
          {footerItems.length > 0 && (
            <div className="grid grid-cols-2 gap-6">
              <FooterNavigation footerItems={footerItems} lang={lang} itemClassName="flex flex-col items-start gap-3" />
              <div className="flex flex-col items-start gap-3">
                <FooterSocialSection iconList={iconList} />

                {/* Contact Information */}
                {contactInfo && (
                  <div className="flex flex-col gap-2 overflow-hidden">
                    {contactInfo.address && (
                      <div className="flex items-start gap-2 overflow-hidden">
                        <Image
                          src={
                            (contactInfo?.locationIcon && `${ADMIN_API_CONFIG.API_URL}${contactInfo?.locationIcon}`) ||
                            DefaultImage
                          }
                          width={16}
                          height={20}
                          alt="Location Icon"
                          className="flex-shrink-0 mt-0.5"
                        />
                        <div className="flex-1 min-w-0 sm:max-w-none max-w-[140px]">
                          {contactInfo.addressUrl && (
                            <Link
                              href={ensureProtocol(contactInfo.addressUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#242F3E] text-sm leading-relaxed hover:text-[#4A9B4A] transition-colors cursor-pointer break-words"
                            >
                              {contactInfo.address}
                            </Link>
                          )}
                          {!contactInfo.addressUrl && (
                            <span className="text-[#242F3E] text-sm leading-relaxed break-words">
                              {contactInfo.address}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {contactInfo.phone && (
                      <div className="flex items-center gap-2">
                        <Image
                          src={
                            (contactInfo?.phoneIcon && `${ADMIN_API_CONFIG.API_URL}${contactInfo?.phoneIcon}`) ||
                            DefaultImage
                          }
                          width={16}
                          height={16}
                          alt="Phone Icon"
                          className="flex-shrink-0"
                        />
                        <a
                          href={"tel:" + contactInfo.phone}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#242F3E] text-sm hover:text-[#4A9B4A] transition-colors cursor-pointer"
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    )}
                    {contactInfo.email && (
                      <div className="flex items-center gap-2">
                        <Image
                          src={
                            (contactInfo?.emailIcon && `${ADMIN_API_CONFIG.API_URL}${contactInfo?.emailIcon}`) ||
                            DefaultImage
                          }
                          width={17}
                          height={14}
                          alt="Email Icon"
                          className="flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 sm:max-w-none max-w-[140px]">
                          <a
                            href={"mailto:" + contactInfo.email}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#242F3E] text-sm hover:text-[#4A9B4A] transition-colors cursor-pointer break-words whitespace-normal"
                          >
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Copyright Section - mobile only to avoid desktop duplicate */}
        <div className="w-full bg-[#0C9443] py-3 px-[0.6875rem] sm:hidden">
          <p className="text-white text-xs text-center font-normal italic">
            {copyRight || "Copyright 2024 © Cào Cào Adventures. All Rights Reserved"}
          </p>
        </div>
      </>
    )
  },
}
